/**
 * Brauzerdagi qidiruv (2026-11-03): backend `core.textsearch` bilan bir xil qoida -- tanlovlar ichidagi qidiruv, People sahifasi,
 * ⌘K buyruqlari. Kichik harf, urg'usiz, tutuq belgilarisiz (' ` ʻ ʼ ‘ ’ ´), kirill so'rov lotinga o'giriladi (o'zbekcha va
 * inglizcha uslub). Avval aniq (har so'z bor, tartib muhim emas); aniq topilmasa -- o'xshashlari (trigram, pg_trgm
 * `word_similarity` kabi, chegara 0.4, so'rov 4+ belgi; bir nechta so'z -- har so'z alohida). Test: textsearch.test.ts.
 */

const QUOTES = /['`ʻʼ‘’´]/g;
const MARKS = /[̀-ͯ]/g;
const CYR = /[Ѐ-ӿ]/;

const UZ: Record<string, string> = {
  а: "a", б: "b", в: "v", г: "g", д: "d", е: "e", ё: "yo", ж: "j", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m", н: "n", о: "o",
  п: "p", р: "r", с: "s", т: "t", у: "u", ф: "f", х: "x", ц: "ts", ч: "ch", ш: "sh", щ: "sh", ъ: "", ы: "i", ь: "", э: "e", ю: "yu",
  я: "ya", ў: "o", қ: "q", ғ: "g", ҳ: "h",
};
const EN: Record<string, string> = { ...UZ, ж: "zh", х: "kh", щ: "shch", қ: "k", ҳ: "kh" };

export const SIMILAR_THRESHOLD = 0.4;
export const SIMILAR_MIN_LENGTH = 4;

/** Qidiruv uchun tozalangan matn (backend `search_norm` bilan bir xil) */
export function normalize(s: string): string {
  return s.toLowerCase().normalize("NFD").replace(MARKS, "").replace(QUOTES, "").replace(/\s+/g, " ").trim();
}

function translit(s: string, table: Record<string, string>): string {
  return [...s.toLowerCase()].map((ch) => table[ch] ?? ch).join("");
}

/** So'rov variantlari: asli va (kirill bo'lsa) lotin o'girmalari, tozalangan */
export function queryVariants(query: string): string[] {
  const forms = CYR.test(query) ? [query, translit(query, UZ), translit(query, EN)] : [query];
  return [...new Set(forms.map(normalize).filter(Boolean))];
}

/** Matn ham kirill bo'lsa lotinga (saqlangan nom kirillda yozilgan bo'lishi mumkin) */
function textForm(text: string): string {
  const n = normalize(text);
  return CYR.test(n) ? `${n} ${normalize(translit(n, UZ))}` : n;
}

function directMatch(text: string, variants: string[]): boolean {
  return variants.some((v) => text.includes(v) || v.split(" ").every((w) => text.includes(w)));
}

/** So'z trigrammalari tartib bilan (pg_trgm kabi: boshida ikki, oxirida bitta bo'shliq) */
function trigrams(text: string): string[] {
  const out: string[] = [];
  for (const word of text.split(" ").filter(Boolean)) {
    const padded = `  ${word} `;
    for (let i = 0; i + 3 <= padded.length; i++) out.push(padded.slice(i, i + 3));
  }
  return out;
}

/** pg_trgm `word_similarity`: so'rov trigrammalari va matndagi eng mos ketma-ket trigramma oralig'i o'xshashligi (Jaccard) */
export function wordSimilarity(query: string, text: string): number {
  const q = new Set(trigrams(query));
  if (q.size === 0) return 0;
  const seq = trigrams(text);
  let best = 0;
  for (let i = 0; i < seq.length; i++) {
    const window = new Set<string>();
    let shared = 0;
    for (let j = i; j < seq.length; j++) {
      if (!window.has(seq[j])) {
        window.add(seq[j]);
        if (q.has(seq[j])) shared++;
      }
      best = Math.max(best, shared / (q.size + window.size - shared));
    }
  }
  return best;
}

/** O'xshashlik (backend `similar_q` bilan bir xil): bitta so'z -- ibora; bir nechta -- har so'z alohida (eng zaifi), 3 harfdan
 * qisqa so'z aniq bo'lsin. Chegaradan past -- 0 */
function similarScore(query: string, text: string): number {
  const words = query.split(" ");
  if (words.length === 1) return wordSimilarity(query, text);
  let worst = 1;
  for (const w of words) {
    const s = w.length >= 3 ? wordSimilarity(w, text) : text.includes(w) ? 1 : 0;
    if (s < SIMILAR_THRESHOLD) return 0;
    worst = Math.min(worst, s);
  }
  return worst;
}

export interface Found<T> {
  items: T[];
  /** Aniq topilmadi -- o'xshashlari (eng yaqini birinchi) */
  similar: boolean;
}

/** Ro'yxatdan qidirish: avval aniq (asl tartibda), bo'lmasa o'xshashlari (o'xshashlik bo'yicha) */
export function findItems<T>(items: T[], query: string, getText: (item: T) => string): Found<T> {
  const variants = queryVariants(query);
  if (!query.trim()) return { items, similar: false };
  if (variants.length === 0) return { items: [], similar: false };
  const texts = items.map((it) => textForm(getText(it)));
  const direct = items.filter((_, i) => directMatch(texts[i], variants));
  if (direct.length) return { items: direct, similar: false };
  const long = variants.filter((v) => v.replace(/ /g, "").length >= SIMILAR_MIN_LENGTH);
  if (!long.length) return { items: [], similar: false };
  const scored = items
    .map((it, i) => ({ it, score: Math.max(...long.map((v) => similarScore(v, texts[i]))) }))
    .filter((x) => x.score >= SIMILAR_THRESHOLD)
    .sort((a, b) => b.score - a.score);
  return { items: scored.map((x) => x.it), similar: scored.length > 0 };
}

/** Bitta matn so'rovga to'g'ridan-to'g'ri mosmi (⌘K buyruqlari kabi qisqa ro'yxatlar) */
export function matchesText(text: string, query: string): boolean {
  const variants = queryVariants(query);
  return !query.trim() || (variants.length > 0 && directMatch(textForm(text), variants));
}
